import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import TablePagination from '@mui/material/TablePagination';
import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { FieldArray, FormikErrors, FormikProvider, useFormik } from 'formik';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SaveIcon from '@mui/icons-material/Save';
import { alpha } from '@mui/material/styles';
import { DecimalNumberField } from '../../components/DecimalNumberField';
import { CARD_BORDER, SectionCard } from '../../components/Section';
import { FakeButtonLink } from '../../components/FakeLink';
import COLORS from '../../constants/colors';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { indexFundBulkUpdateNavFormFields } from '../../forms/fields/formFields';
import { indexFundBulkUpdateNavFormSchema } from '../../forms/validationSchema/formSchema';
import { useAppDispatch } from '../../redux/hooks';
import {
  bulkUpdateIndexFundNavs,
  cleanIndexFunds,
  fetchIndexFunds,
} from '../../redux/modules/indexFund/indexFund.actions';
import { indexFundSelector } from '../../redux/modules/indexFund/indexFund.selectors';
import { locations } from '../../routes/locations';
import { BulkUpdateIndexFundNavs, IndexFund } from '../../types/indexFund';
import { isDispatchApiSuccess } from '../../utils/api';
import * as permissions from '../../utils/permissions';
import { ProtectedContent } from '../../components/ProtectedContent';

/** Column labels, matching the header treatment used by the shared data grids. */
const navHeaderSx = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: COLORS.DARK_GREY,
};

export function IndexFundNavList() {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { indexFunds, totalIndexFunds, fetchStatus, updateStatus } = useSelector(indexFundSelector);
  const isLoading = fetchStatus === fetchStatuses.pending;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const isSaveLoading = isLoading || updateStatus === fetchStatuses.pending;

  const formik = useFormik({
    initialValues: {
      [indexFundBulkUpdateNavFormFields.items]: [] as BulkUpdateIndexFundNavs[],
    },
    validationSchema: indexFundBulkUpdateNavFormSchema,
    onSubmit: async (formValues) => {
      const items = formValues.items.map((i) => ({
        ...i,
        nav: Number(i.nav) || 0.01,
        originalNav: Number(i.originalNav),
      }));

      const res = await dispatch(bulkUpdateIndexFundNavs(items));

      if (isDispatchApiSuccess(res)) {
        dispatch(fetchIndexFunds({ limit: rowsPerPage, offset: page * rowsPerPage }));
        enqueueSnackbar('Navs updated successfully!', { variant: 'success' });
      } else {
        enqueueSnackbar('Navs update failed!', { variant: 'error' });
      }
    },
  });
  const { handleSubmit, values, handleChange, touched, errors, setValues } = formik;

  useEffect(() => {
    dispatch(fetchIndexFunds({ limit: rowsPerPage, offset: page * rowsPerPage }));

    return () => {
      dispatch(cleanIndexFunds());
    };
  }, [page, rowsPerPage]);

  useEffect(() => {
    const items = indexFunds.map((item) => ({
      id: item.id,
      nav: item.netAssetValuePerUnit,
      originalNav: item.originalNav,
    }));

    setValues({
      [indexFundBulkUpdateNavFormFields.items]: items,
    });
  }, [indexFunds]);

  const getIndexFundById = (id: number): IndexFund | undefined => indexFunds.find((item) => item.id === id);

  const renderForm = () => (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={12}>
          <SectionCard
            title="Fund NAVs"
            description="Bulk-update net asset values"
            disablePadding
            sx={{ '& .MuiTypography-root': { fontSize: '0.8125rem' } }}
          >
            <FormikProvider value={formik}>
              <FieldArray
                name="items"
                render={() => (
                  <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                    <Grid
                      container
                      item
                      spacing={3}
                      component={ListItem}
                      sx={{ backgroundColor: alpha(COLORS.MAIN_DARK, 0.02), borderBottom: `1px solid ${CARD_BORDER}` }}
                    >
                      <Grid item xs={1} md={1}>
                        <Typography sx={navHeaderSx}>Id</Typography>
                      </Grid>
                      <Grid item xs={1} md={2}>
                        <Typography sx={navHeaderSx}>Source</Typography>
                      </Grid>
                      <Grid item xs={1} md={1}>
                        <Typography sx={navHeaderSx}>Code</Typography>
                      </Grid>
                      <Grid item xs={11} md={3}>
                        <Typography sx={navHeaderSx}>Fund Name</Typography>
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <Typography sx={navHeaderSx}>Original price</Typography>
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <Typography sx={navHeaderSx}>FX Rate</Typography>
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <Typography sx={navHeaderSx}>SAR price</Typography>
                      </Grid>
                      <Grid item xs={2} md={2}>
                        <Typography sx={navHeaderSx}>Last Update</Typography>
                      </Grid>
                    </Grid>
                    {values[indexFundBulkUpdateNavFormFields.items].map((item, index) => (
                      <React.Fragment key={item.id}>
                        <Grid
                          container
                          item
                          spacing={3}
                          component={ListItem}
                          sx={{ borderBottom: `1px solid ${alpha(COLORS.MAIN_DARK, 0.06)}` }}
                        >
                          <Grid item xs={1} md={1}>
                            {isLoading ? (
                              <Skeleton variant="rectangular" width="100%" height={80} />
                            ) : (
                              <FakeButtonLink
                                startIcon={<OpenInNewIcon />}
                                href={locations.indexFund(getIndexFundById(item.id!)?.id)}
                              >
                                {getIndexFundById(item.id!)?.id}
                              </FakeButtonLink>
                            )}
                          </Grid>
                          <Grid item xs={1} md={2}>
                            {isLoading ? (
                              <Skeleton variant="rectangular" width="100%" height={80} />
                            ) : (
                              <Typography>{getIndexFundById(item.id!)?.source?.name}</Typography>
                            )}
                          </Grid>
                          <Grid item xs={1} md={1}>
                            {isLoading ? (
                              <Skeleton variant="rectangular" width="100%" height={80} />
                            ) : (
                              <Typography>{getIndexFundById(item.id!)?.code}</Typography>
                            )}
                          </Grid>
                          <Grid item xs={11} md={3}>
                            {isLoading ? (
                              <Skeleton variant="rectangular" width="100%" height={80} />
                            ) : (
                              <Typography>{getIndexFundById(item.id!)?.nameEn}</Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} md={1}>
                            {isLoading ? (
                              <Skeleton variant="rectangular" width="100%" height={80} />
                            ) : (
                              <FormControl
                                fullWidth
                                error={
                                  touched.items?.[index]?.originalNav &&
                                  !!(errors.items?.[index] as FormikErrors<BulkUpdateIndexFundNavs>)?.nav
                                }
                              >
                                <DecimalNumberField
                                  size="small"
                                  fullWidth
                                  type="number"
                                  id={`items.${index}.originalNav`}
                                  name={`items.${index}.originalNav`}
                                  label="Original Nav"
                                  value={item.originalNav}
                                  onChange={handleChange}
                                  error={
                                    touched.items?.[index]?.originalNav &&
                                    !!(errors.items?.[index] as FormikErrors<BulkUpdateIndexFundNavs>)?.originalNav
                                  }
                                  helperText={
                                    touched.items?.[index]?.originalNav &&
                                    (errors.items?.[index] as FormikErrors<BulkUpdateIndexFundNavs>)?.originalNav
                                  }
                                  maximumFractionDigits={6}
                                  autoComplete="index-fund-originalNav"
                                />
                              </FormControl>
                            )}
                          </Grid>
                          <Grid item xs={11} md={1}>
                            {isLoading ? (
                              <Skeleton variant="rectangular" width="100%" height={80} />
                            ) : (
                              <Typography>{getIndexFundById(item.id!)?.fxRate}</Typography>
                            )}
                          </Grid>
                          <Grid item xs={11} md={1}>
                            {isLoading ? (
                              <Skeleton variant="rectangular" width="100%" height={80} />
                            ) : (
                              <Typography>{getIndexFundById(item.id!)?.netAssetValuePerUnit}</Typography>
                            )}
                          </Grid>
                          <Grid item xs={2} md={2}>
                            {isLoading ? (
                              <Skeleton variant="rectangular" width="100%" height={80} />
                            ) : (
                              <Typography>
                                {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                                {new Date((getIndexFundById(item.id!)?.updatedAt as string) || '').toLocaleString(
                                  'en-US',
                                )}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              />
              {!!errors.items && typeof errors.items === 'string' && (
                <FormHelperText error>{errors.items}</FormHelperText>
              )}
            </FormikProvider>
            <TablePagination
              component="div"
              sx={{ borderTop: `1px solid ${CARD_BORDER}` }}
              count={totalIndexFunds || 0}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </SectionCard>
        </Grid>
      </Grid>
      <ProtectedContent items={[permissions.IndexFund.navBulkUpdate]}>
        <Grid>
          <Box
            sx={{
              mt: '16px',
              p: '14px 16px',
              display: 'flex',
              justifyContent: 'flex-end',
              borderRadius: '12px',
              border: `1px solid ${CARD_BORDER}`,
              backgroundColor: COLORS.WHITE,
            }}
          >
            <LoadingButton
              type="submit"
              color="primary"
              variant="contained"
              endIcon={<SaveIcon />}
              loading={isSaveLoading}
              loadingPosition="end"
            >
              Save
            </LoadingButton>
          </Box>
        </Grid>
      </ProtectedContent>
    </form>
  );

  return <Box mt={2}>{renderForm()}</Box>;
}
